import { useCancelTransferOffer } from '@hooks/mutations/registry/transfer/useCancelTransferOffer';
import { useCancelTransferRequest } from '@hooks/mutations/registry/transfer/useCancelTransferRequest';
import { useRejectTransferOffer } from '@hooks/mutations/registry/transfer/useRejectTransferOffer';
import { useRejectTransferRequest } from '@hooks/mutations/registry/transfer/useRejectTransferRequest';
import { useRejectTransferInstruction } from '@hooks/mutations/registry/transferInstruction/useRejectTransferInstruction';
import { useWithdrawTransferInstruction } from '@hooks/mutations/registry/transferInstruction/useWithdrawTransferInstruction';
import { useCallback } from 'react';
import { TransferInstruction } from '@daml.js/splice-api-token-transfer-instruction/lib/Splice/Api/Token/TransferInstructionV1';
import {
  TransferOffer,
  TransferRequest,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Transfer';
import { ContractId } from '@daml/types';
import { ChoiceDialogProps } from '../../../components/ChoiceDialog';
import { RejectDialog } from '../../../components/Dialog/RejectDialog';
import { useParties } from '../../../contexts/PartiesContext';
import { useAcceptTransferOffer } from '../../../hooks/mutations/registry/transfer/useAcceptTransferOffer';
import { useAcceptTransferInstruction } from '../../../hooks/mutations/registry/transferInstruction/useAcceptTransferInstruction';
import { AcceptTransferRequestDialog } from './AcceptTransferRequestDialog';
import { Row } from './types';

export const useCreateTransferActions = (onClose: () => void, r?: Row) => {
  const rejectTransferOffer = useRejectTransferOffer();
  const rejectTransferRequest = useRejectTransferRequest();
  const cancelTransferOffer = useCancelTransferOffer();
  const cancelTransferRequest = useCancelTransferRequest();
  const acceptTransferOffer = useAcceptTransferOffer();
  const acceptTransferInstruction = useAcceptTransferInstruction();
  const withdrawTransferInstruction = useWithdrawTransferInstruction();
  const rejectTransferInstruction = useRejectTransferInstruction();
  const { party } = useParties();

  const onAcceptTransferInstruction = async () => {
    if (!r) {
      throw new Error('No row');
    }
    await acceptTransferInstruction({
      registrar: r.payload.registrar,
      transferCid: r.contractId as ContractId<TransferInstruction>,
    });
    onClose();
  };
  const onWithdrawTransferInstruction = async () => {
    if (!r) {
      throw new Error('No row');
    }
    await withdrawTransferInstruction({
      registrar: r.payload.registrar,
      transferCid: r.contractId as ContractId<TransferInstruction>,
    });
    onClose();
  };
  const onRejectTransferInstruction = async (reason: string) => {
    if (!r) {
      throw new Error('No row');
    }
    await rejectTransferInstruction({
      registrar: r.payload.registrar,
      transferCid: r.contractId as ContractId<TransferInstruction>,
      reason,
    });
    onClose();
  };
  const rejectTransferOff = useCallback(
    async (reason: string) => {
      if (!r) {
        throw new Error('No row');
      }

      await rejectTransferOffer({
        cid: r.contractId as ContractId<TransferOffer>,
        argument: { reason },
      });
      onClose();
    },
    [rejectTransferOffer, r, onClose],
  );

  const cancelTransferOff = useCallback(async () => {
    if (!r) {
      throw new Error('No row');
    }

    await cancelTransferOffer({ cid: r.contractId as ContractId<TransferOffer>, argument: {} });
    onClose();
  }, [cancelTransferOffer, r, onClose]);

  const rejectTransferReq = useCallback(
    async (reason: string) => {
      if (!r) {
        throw new Error('No row');
      }
      await rejectTransferRequest({
        cid: r.contractId as ContractId<TransferRequest>,
        argument: { reason },
      });
      onClose();
    },
    [rejectTransferRequest, onClose, r],
  );

  const cancelTransferReq = useCallback(async () => {
    if (!r) {
      throw new Error('No row');
    }

    await cancelTransferRequest({ cid: r.contractId as ContractId<TransferRequest>, argument: {} });
    onClose();
  }, [cancelTransferRequest, onClose, r]);
  const acceptTransferOff = useCallback(async () => {
    if (!r) {
      throw new Error('No row');
    }

    await acceptTransferOffer({
      cid: r.contractId as ContractId<TransferOffer>,
      argument: { receiverLabel: '' },
    });
    onClose();
  }, [acceptTransferOffer, onClose, r]);

  const actions: ChoiceDialogProps['actions'] = [];
  if (!r) {
    return actions;
  }
  const isReceiver = r.payload.receiver === party;
  const isSender = r.payload.sender === party;
  if (r.state === 'Offered') {
    if (isReceiver) {
      actions.push(
        { name: 'Accept', action: acceptTransferOff },
        {
          name: 'Reject',
          color: 'error',
          form: (
            <RejectDialog
              open
              disableDialog
              onClose={onClose}
              onReject={(reason: string) => rejectTransferOff(reason)}
            />
          ),
        },
      );
    }
    if (isSender) {
      actions.push({ name: 'Cancel', action: cancelTransferOff });
    }
  }
  if (r.state === 'Requested') {
    if (isSender) {
      actions.push(
        {
          name: 'Accept',
          form: <AcceptTransferRequestDialog open onClose={onClose} request={r} />,
        },
        {
          name: 'Reject',
          form: (
            <RejectDialog
              open
              disableDialog
              onReject={(reason: string) => rejectTransferReq(reason)}
              onClose={onClose}
            />
          ),
        },
      );
    }
    if (isReceiver) {
      actions.push({ name: 'Cancel', action: cancelTransferReq });
    }
  }
  if (r.state === 'Offered2step') {
    if (isReceiver) {
      actions.push(
        {
          name: 'Accept',
          action: onAcceptTransferInstruction,
        },
        {
          name: 'Reject',
          color: 'error',
          form: (
            <RejectDialog
              open
              disableDialog
              onClose={onClose}
              onReject={(reason: string) => onRejectTransferInstruction(reason)}
            />
          ),
        },
      );
    }
    if (isSender) {
      actions.push({
        name: 'Withdraw',
        action: onWithdrawTransferInstruction,
      });
    }
  }

  return actions;
};
